import { useMemo } from "react";
import { Link } from "react-router-dom";
import { getAllTasks } from "../../lib/phases";
import { AlertCircle, Clock, CheckCircle2 } from "lucide-react";

function daysLabel(d) {
  if (d < 0) return `${Math.abs(d)}d overdue`;
  if (d === 0) return "Due today";
  if (d === 1) return "Due tomorrow";
  return `In ${d}d`;
}

function UpcomingRow({ item, tone }) {
  return (
    <Link
      to={`/listing/${item.listingId}`}
      data-testid={`upcoming-item-${item.listingId}`}
      className="block rounded-md border border-stone-200 bg-white p-3.5 hover:border-stone-300 hover:bg-stone-50/50 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-stone-900 line-clamp-1 leading-snug">{item.taskLabel}</p>
          <p className="text-xs text-stone-500 mt-1 line-clamp-1">
            {item.address || "Untitled"} · {item.phase}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide whitespace-nowrap ${
            tone === "overdue"
              ? "bg-rose-100 text-rose-700"
              : item.delta <= 1
              ? "bg-amber-100 text-amber-800"
              : "bg-stone-100 text-stone-600"
          }`}
        >
          {daysLabel(item.delta)}
        </span>
      </div>
    </Link>
  );
}

export default function UpcomingWidget({ listings }) {
  const { upcoming, overdue } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const knownTasks = getAllTasks();
    const up = [];
    const od = [];
    (listings || []).forEach((l) => {
      const tasksState = l.tasks || {};
      // Iterate KNOWN tasks (not arbitrary keys on the listing) so any orphan
      // state from prior schema changes can never surface here.
      knownTasks.forEach((task) => {
        const t = tasksState[task.id];
        if (!t || t.done || !t.date) return;
        const due = new Date(t.date);
        due.setHours(0, 0, 0, 0);
        const delta = Math.round((due - today) / 86400000);
        const item = {
          listingId: l.id,
          taskId: task.id,
          address: l.property_address,
          taskLabel: task.label,
          phase: task.phaseTitle,
          date: t.date,
          delta,
        };
        if (delta < 0) od.push(item);
        else if (delta <= 7) up.push(item);
      });
    });
    up.sort((a, b) => a.delta - b.delta);
    od.sort((a, b) => a.delta - b.delta);
    return { upcoming: up, overdue: od };
  }, [listings]);

  return (
    <div className="sticky top-28 space-y-5">
      <div data-testid="overdue-widget" className="rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-4 w-4 text-rose-600" />
          <h3 className="text-sm font-heading font-semibold tracking-tight text-stone-900">
            Overdue
          </h3>
          <span className="ml-auto text-xs tabular-nums text-stone-500 font-heading">{overdue.length}</span>
        </div>
        {overdue.length === 0 ? (
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            Nothing overdue — great work.
          </div>
        ) : (
          <div className="space-y-2">
            {overdue.slice(0, 6).map((it) => (
              <UpcomingRow key={`${it.listingId}-${it.taskId}`} item={it} tone="overdue" />
            ))}
          </div>
        )}
      </div>

      <div data-testid="upcoming-widget" className="rounded-md border border-stone-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-stone-600" />
          <h3 className="text-sm font-heading font-semibold tracking-tight text-stone-900">
            Upcoming · 7 days
          </h3>
          <span className="ml-auto text-xs tabular-nums text-stone-500 font-heading">{upcoming.length}</span>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-xs text-stone-500 leading-relaxed">
            No tasks due in the next week. Set deadlines from any listing checklist.
          </p>
        ) : (
          <div className="space-y-2">
            {upcoming.slice(0, 8).map((it) => (
              <UpcomingRow key={`${it.listingId}-${it.taskId}`} item={it} tone="upcoming" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
